
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function AIApproach() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const pillars = [
    {
      icon: 'ri-eye-line',
      title: 'Transparent by Default',
      description: 'We don\'t hide our AI usage behind closed doors. Every client knows exactly how and where AI is involved in their campaigns. No smoke and mirrors — just honest, modern marketing.',
    },
    {
      icon: 'ri-speed-line',
      title: 'Speed Without Shortcuts',
      description: 'AI handles the repetitive, time-consuming tasks — scheduling, data crunching, first-draft copy — so our team can focus on the creative strategy and human insight that actually moves the needle.',
    },
    {
      icon: 'ri-money-pound-circle-line',
      title: 'Savings Passed to You',
      description: 'When AI saves us time, we don\'t pocket the difference. Those efficiencies go straight back to you through fairer pricing and faster turnaround. Smarter systems, not bigger invoices.',
    },
    {
      icon: 'ri-brain-line',
      title: 'Human Thinking First',
      description: 'AI is a tool, not a replacement. Every strategy, creative direction, and client relationship is driven by real people who understand your brand, your audience, and your goals.',
    },
  ];

  const workflow = [
    {
      step: '01',
      label: 'Research & Insights',
      detail: 'AI analyses trends, competitors, and audience data at scale to surface opportunities humans would miss.',
    },
    {
      step: '02',
      label: 'Human Strategy',
      detail: 'We interpret the data, craft your brand narrative, and build a bespoke campaign plan.',
    },
    {
      step: '03',
      label: 'AI-Assisted Creation',
      detail: 'Content drafts, image variations, and copy options are generated rapidly — then refined by our creative team.',
    },
    {
      step: '04',
      label: 'Review & Publish',
      detail: 'Every piece is reviewed by a human before it goes live. Quality, tone, and brand alignment are never automated.',
    },
  ];

  return (
    <section
      ref={ref}
      id="ai-approach"
      className={`py-28 bg-white transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <div className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00989e]/10 text-[#00989e] font-semibold text-sm mb-6">
              <i className="ri-robot-2-line text-base"></i>
              Post-AI Methodology
            </span>
            <h2
              className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Built for the <br />
              <span className="text-[#00989e]">AI era.</span> Led by humans.
            </h2>
          </div>
          <div className={`flex items-end transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <p className="text-gray-600 text-lg leading-relaxed">
              unuu is a fully post-AI business. That doesn't mean we've replaced people with machines — it means we've properly understood how AI can improve speed, consistency, and quality without losing the human thinking that good marketing still needs. We use AI openly, responsibly, and always in your favour.
            </p>
          </div>
        </div>

        {/* Four Pillars */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-28">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className={`group relative bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-[#00989e]/40 hover:shadow-xl transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible ? `${index * 100 + 200}ms` : '0ms' }}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#00989e] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${pillar.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {pillar.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* How It Works - Workflow */}
        <div className={`transition-all duration-700 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <div className="text-center mb-14">
            <h3
              className="text-3xl font-bold text-slate-900 mb-3"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              How AI Fits Into Our Workflow
            </h3>
            <p className="text-gray-600 max-w-xl mx-auto">
              AI amplifies every stage — but humans steer the ship at every turn.
            </p>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#00989e]/20 via-[#00989e] to-[#00989e]/20"></div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workflow.map((item, index) => (
                <div
                  key={index}
                  className={`text-center transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: isVisible ? `${index * 120 + 500}ms` : '0ms' }}
                >
                  <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-white border-2 border-[#00989e] mb-6 shadow-lg shadow-[#00989e]/10">
                    <span
                      className="text-3xl font-bold text-[#00989e]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {item.step}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {item.label}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    {item.detail}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Statement */}
        <div className={`mt-20 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 lg:p-14 flex flex-col lg:flex-row items-center gap-10 transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="flex-1">
            <h3
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We don't do rocket science.
            </h3>
            <p className="text-white/80 text-lg leading-relaxed">
              We simply help people find you when they need you. We know that you are likely to be referred by customers, friends and family, we help this process by enhancing what people see when they search for you. Keeping it simple means faster delivery, consistent output, and fair, transparent pricing. You benefit from smarter systems - not bigger invoices.
            </p>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#00989e] text-white font-bold rounded-full hover:bg-[#007a7f] transition-all hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            <i className="ri-chat-smile-2-line text-xl"></i>
            See How We Work
          </a>
        </div>
      </div>
    </section>
  );
}
